import {
  Box,
  Button,
  Checkbox,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useEffect } from 'react';
import usePackageFees from '../../../../../../hooks/orders/usePackageFees';

const OrderPackagesList = ({
  orderDetails,
  selectedPackages,
  setSelectedPackages,
  retractionType,
  setRetractionType,
  disabled,
  setDisabled,
  productsAmount,
  selectedProducts,
  setSelectedProducts,
}) => {
  const orderRef = orderDetails?.reference;
  const { packageFees, isLoading, _error, _mutate } = usePackageFees(orderRef);
  const _theme = useTheme();
  const handleCheckboxChange = (item, lineIndex, itemIndex) => {
    const itemReference = `${lineIndex}-${itemIndex}-${item?.reference}`;

    if (selectedPackages.some((pkg) => pkg.itemReference === itemReference)) {
      const updatedPackages = selectedPackages.filter(
        (pkg) => pkg.itemReference !== itemReference
      );

      if (updatedPackages.length === 0) {
        return;
      }
      setSelectedPackages(updatedPackages);
      if (
        updatedPackages.length !==
        packageFees?.lines?.flatMap((line) => line?.items || []).length
      ) {
        setRetractionType('partial');
      }
    } else {
      const updatedPackages = [...selectedPackages, { ...item, itemReference }];
      setSelectedPackages(updatedPackages);
      if (
        updatedPackages.length ===
        packageFees?.lines?.flatMap((line) => line?.items || []).length
      ) {
        setRetractionType('total');
      }
    }
  };

  const handleLineSelection = (line, lineIndex) => {
    const lineItems = line?.items || [];
    const lineItemReferences = lineItems.map((item, itemIndex) => ({
      ...item,
      itemReference: `${lineIndex}-${itemIndex}-${item?.reference}`,
    }));

    const allLineItemsSelected = lineItemReferences.every((item) =>
      selectedPackages.some((pkg) => pkg.itemReference === item.itemReference)
    );

    if (allLineItemsSelected) {
      const updatedPackages = selectedPackages.filter(
        (pkg) =>
          !lineItemReferences.some(
            (item) => item.itemReference === pkg.itemReference
          )
      );
      if (updatedPackages.length === 0) {
        return;
      }
      setSelectedPackages(updatedPackages);

      // Mise à jour du retractionType
      const totalPackages = packageFees?.lines?.flatMap(
        (line) => line?.items || []
      ).length;
      setRetractionType(
        updatedPackages.length === totalPackages ? 'total' : 'partial'
      );

      setSelectedProducts(
        selectedProducts.filter((product) => product.lineIndex !== lineIndex)
      );
    } else {
      const newPackages = [
        ...selectedPackages.filter(
          (pkg) =>
            !lineItemReferences.some(
              (item) => item.itemReference === pkg.itemReference
            )
        ),
        ...lineItemReferences,
      ];
      setSelectedPackages(newPackages);

      // Mise à jour du retractionType
      const totalPackages = packageFees?.lines?.flatMap(
        (line) => line?.items || []
      ).length;
      setRetractionType(
        newPackages.length === totalPackages ? 'total' : 'partial'
      );

      setSelectedProducts([
        ...selectedProducts.filter(
          (product) => product.lineIndex !== lineIndex
        ),
        {
          lineIndex,
          productTitle: line?.productTitle,
          productsAmount: line?.productAmount,
        },
      ]);
    }
  };

  const handleAll = () => {
    const allPackages =
      packageFees?.lines?.flatMap((line, lineIndex) =>
        (line?.items || []).map((item, itemIndex) => ({
          ...item,
          itemReference: `${lineIndex}-${itemIndex}-${item?.reference}`,
        }))
      ) || [];

    if (selectedPackages.length === allPackages.length) {
      return;
    } else {
      setSelectedPackages(allPackages);
      setRetractionType('total');
    }
  };

  useEffect(() => {
    if (!disabled) {
      const linesCount = packageFees?.lines?.length || 0;
      if (linesCount < 2) {
        setDisabled(true);
      }
    }
  }, [disabled, packageFees]);

  useEffect(() => {
    if (retractionType === 'total') {
      const allPackages =
        packageFees?.lines?.flatMap((line, lineIndex) =>
          (line?.items || []).map((item, itemIndex) => ({
            ...item,
            itemReference: `${lineIndex}-${itemIndex}-${item?.reference}`,
          }))
        ) || [];

      setSelectedPackages(allPackages);

      // Ajouter toutes les lignes de produits
      const allProducts =
        packageFees?.lines?.map((line, lineIndex) => ({
          lineIndex,
          productTitle: line?.productTitle,
          productAmount: line?.productAmount,
        })) || [];
      setSelectedProducts(allProducts);
    }

    if (
      retractionType === 'partial' &&
      packageFees?.lines?.flatMap((line) => line?.items || []).length ===
        selectedPackages.length
    ) {
      setSelectedPackages([]);
      setSelectedProducts([]); // Vider aussi les produits sélectionnés
    }
  }, [retractionType, packageFees]);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          color="primary"
          sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
        >
          Colis concernés
        </Typography>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={handleAll}
          disabled={
            selectedPackages.length ===
            packageFees?.lines?.flatMap((line) =>
              line?.items?.map((item) => item)
            ).length
          }
          sx={{
            fontSize: '0.6em',
            padding: '0.2em 0.5em',
            height: '2.5em',
            mb: 1,
          }}
        >
          Tout sélectionner
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              }}
            >
              <TableCell sx={{ fontWeight: 'bold' }}>Sélectionner</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Titre</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Référence</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Poids</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Prix</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={4}>
                      <Skeleton variant="text" height={40} />
                    </TableCell>
                  </TableRow>
                ))
              : packageFees?.lines?.map((line, lineIndex) => (
                  <React.Fragment key={`line-group-${lineIndex}`}>
                    <TableRow
                      sx={{
                        backgroundColor: line?.isPackTitle
                          ? 'rgba(0, 0, 0, 0.1)'
                          : 'rgba(0, 0, 0, 0.03)',
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '0.9em',
                          padding: '0 0.5em',
                          paddingLeft: '8px',
                        }}
                        colSpan={4}
                      >
                        <Box sx={{ display: 'flex' }}>
                          {!line?.isPackTitle && (
                            <Checkbox
                              sx={{
                                padding: '0 0.5em',
                                paddingLeft: '8px',
                              }}
                              checked={line?.items?.every((item, itemIndex) =>
                                selectedPackages.some(
                                  (pkg) =>
                                    pkg.itemReference ===
                                    `${lineIndex}-${itemIndex}-${item?.reference}`
                                )
                              )}
                              onChange={() =>
                                handleLineSelection(line, lineIndex)
                              }
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: '0.9em',
                              padding: '0 0.5em',
                              paddingLeft: '8px',
                              fontWeight: 'bold',
                            }}
                          >
                            {line?.productTitle || `Produit ${lineIndex + 1}`}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: '0.9em',
                            padding: '0 0.5em',
                            paddingLeft: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          {line?.productAmount
                            ? `${line?.productAmount}${getSymbolFromCurrency(
                                orderDetails?.currency?.code
                              )}`
                            : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {line?.items?.map((item, itemIndex) => (
                      <TableRow
                        key={`item-${lineIndex}-${itemIndex}-${item?.reference}`}
                      >
                        <TableCell
                          sx={{
                            fontSize: '0.9em',
                            padding: '0 0.5em',
                            paddingLeft: '24px',
                          }}
                        >
                          <Checkbox
                            disabled
                            checked={selectedPackages.some(
                              (pkg) =>
                                pkg.itemReference ===
                                `${lineIndex}-${itemIndex}-${item?.reference}`
                            )}
                            onChange={() =>
                              handleCheckboxChange(item, lineIndex, itemIndex)
                            }
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontSize: '0.9em', padding: '0 0.5em' }}
                          size="small"
                        >
                          {item?.title}
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ fontSize: '0.9em', padding: '0 0.5em' }}
                        >
                          {item?.reference}
                        </TableCell>
                        <TableCell
                          size="small"
                          sx={{ fontSize: '0.9em', padding: '0 0.5em' }}
                        >
                          {item?.rawWeight} kg
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
          </TableBody>
          <TableBody>
            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.05)' }}>
              <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9em' }}>
                {selectedPackages.length} colis sélectionné
                {selectedPackages.length > 1 ? 's' : ''}
              </TableCell>
              <TableCell rowSpan={1} />
              <TableCell colSpan={2} />
              <TableCell colSpan={1}>
                <Typography sx={{ fontWeight: 'bold', fontSize: '0.9em' }}>
                  Total:{' '}
                  {productsAmount
                    ? `${productsAmount}${getSymbolFromCurrency(
                        orderDetails?.currency?.code
                      )}`
                    : ''}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default OrderPackagesList;
